import { FC, PropsWithChildren, ReactElement, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { Combobox, ComboboxOption } from "@/components/_molecules/combobox/combobox";
import { compareFractionalOrder } from "@/lib/compare-fractional-order";

import { useGetPhaseListForWorkspace } from "../hooks/api/use-get-phase-list-for-workspace";
import { Phase } from "../models/phase-model";

import { PhaseSelectDropdownItem } from "./phase-select-dropdown-item";

export interface PhaseSelectDropdownProps extends PropsWithChildren {
  workspaceId: string;
  asChild?: boolean;
  selected?: {
    phaseId: string;
  } | null;
  onSelect?: (phase: { id: string } | null) => void;
  side?: "top" | "right" | "bottom" | "left";
  isReadOnly?: boolean;
}

export const PhaseSelectDropdown: FC<PhaseSelectDropdownProps> = ({
  workspaceId,
  children,
  asChild,
  side,
  isReadOnly,
  onSelect,
  ...props
}) => {
  const { t } = useTranslation("common");
  const [selectedPhase, setSelectedPhase] = useState(props.selected ? props.selected : null);
  const { phaseList } = useGetPhaseListForWorkspace({ workspaceId });

  const comboboxOptions = useMemo(() => {
    const phaseOptions = phaseList
      .slice()
      .sort((a, b) => compareFractionalOrder(a.order, b.order))
      .flatMap((u) => {
        if (!u) {
          return [];
        }
        return {
          id: u.id,
          value: u.name,
          original: u,
        };
      });
    return [{ id: "-1", value: t("notAssigned"), original: null }, ...phaseOptions];
  }, [phaseList, t]);

  const renderItem = (option: ComboboxOption<Phase>): ReactElement => {
    const phase = option.original;

    const selected = (option.id === "-1" && selectedPhase === null) || selectedPhase?.phaseId === option.id;
    return <PhaseSelectDropdownItem phase={phase} selected={selected} />;
  };

  const handleUserSelected = (selectedOption: ComboboxOption<Phase> | null) => {
    if (!selectedOption) {
      return;
    }

    if (selectedOption.id === "-1") {
      setSelectedPhase(null);
      if (onSelect) {
        onSelect(null);
      }
      return;
    }

    setSelectedPhase({ phaseId: selectedOption.id });
    if (onSelect) {
      onSelect(selectedOption);
    }
  };

  useEffect(() => {
    if (props.selected) {
      setSelectedPhase(props.selected);
    }
  }, [props.selected]);

  if (!comboboxOptions) {
    return <></>;
  }

  return (
    <Combobox
      data-testid="phase-select-dropdown"
      asChild={asChild}
      options={comboboxOptions}
      renderItem={renderItem}
      onSelect={handleUserSelected}
      side={side}
      isReadOnly={isReadOnly}
    >
      {children}
    </Combobox>
  );
};
